import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Users, Mail, TrendingUp, BarChart3, Target,
  RefreshCw, Loader2, CheckCircle2, AlertCircle, Eye
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SimulatorAnalyticsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/simulator/analytics`);
      if (!response.ok) throw new Error('Failed to load analytics');
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error('Erreur lors du chargement des analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelBadge = (level) => {
    const styles = {
      'expert': 'bg-emerald-100 text-emerald-700',
      'avance': 'bg-blue-100 text-blue-700',
      'intermediaire': 'bg-amber-100 text-amber-700',
      'debutant': 'bg-red-100 text-red-700'
    };
    const labels = {
      'expert': 'Expert',
      'avance': 'Avancé',
      'intermediaire': 'Intermédiaire',
      'debutant': 'Débutant'
    };
    return <Badge className={styles[level] || 'bg-slate-100 text-slate-700'}>{labels[level] || level}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard/smartfacture')}
        className="text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour
      </Button>

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 p-6 md:p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20">
              <BarChart3 className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Analytics Simulateur</h1>
              <p className="text-violet-200 mt-1">
                Performance du quiz e-facturation 2026
              </p>
            </div>
          </div>
          <Button 
            variant="secondary" 
            onClick={loadAnalytics}
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Quiz complétés</p>
                <p className="text-2xl font-bold text-blue-900">{data?.overview?.total_quiz_completed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Mail className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Emails capturés</p>
                <p className="text-2xl font-bold text-emerald-900">{data?.conversion?.leads_with_email || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Target className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Taux capture</p>
                <p className="text-2xl font-bold text-amber-900">{data?.conversion?.email_capture_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-50 to-violet-100/50 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-500/10">
                <TrendingUp className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-violet-600 font-medium">Conversion</p>
                <p className="text-2xl font-bold text-violet-900">{data?.conversion?.conversion_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Tendance journalière */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Quiz par jour (7 derniers jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.trends?.daily && data.trends.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.trends.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-500">
                Pas encore de données
              </div>
            )}
          </CardContent>
        </Card>

        {/* Distribution par niveau */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-violet-600" />
              Distribution par niveau
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data?.distribution?.by_level && Object.entries(data.distribution.by_level).length > 0 ? (
                Object.entries(data.distribution.by_level).map(([level, count]) => {
                  const total = Object.values(data.distribution.by_level).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  const colors = {
                    expert: 'bg-emerald-500',
                    avance: 'bg-blue-500',
                    intermediaire: 'bg-amber-500',
                    debutant: 'bg-red-500'
                  };
                  return (
                    <div key={level} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="capitalize font-medium">{level}</span>
                        <span className="text-slate-500">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${colors[level] || 'bg-slate-500'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Pas encore de données
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Derniers leads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Derniers leads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.recent_leads && data.recent_leads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Email</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Entreprise</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Score</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Niveau</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Converti</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_leads.map((lead, idx) => (
                    <tr key={lead.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="text-sm">{lead.email || '-'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm">{lead.company_name || '-'}</td>
                      <td className="py-3 px-2">
                        <span className="font-semibold text-blue-600">{lead.score}%</span>
                      </td>
                      <td className="py-3 px-2">{getLevelBadge(lead.level)}</td>
                      <td className="py-3 px-2">
                        {lead.converted ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-slate-300" />
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-500">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Aucun lead pour le moment
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats rapides */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Aujourd'hui</p>
                <p className="text-2xl font-bold">{data?.overview?.quiz_today || 0}</p>
              </div>
              <Eye className="h-8 w-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cette semaine</p>
                <p className="text-2xl font-bold">{data?.overview?.quiz_this_week || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-500/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Score moyen</p>
                <p className="text-2xl font-bold">{data?.overview?.average_score || 0}%</p>
              </div>
              <Target className="h-8 w-8 text-violet-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SimulatorAnalyticsPage;
